import { useEffect, useRef, useState } from "react";
import { Send, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import API from "../api";

const QUICK = [
  "What can our group do on the island?",
  "How many guests can stay?",
  "How do we book?",
];

const HELLO =
  "Welcome to Collanmore Island Lodge. Ask me anything about the island, the lodge or planning your stay — I'm here to help.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: HELLO }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => {
    let id = localStorage.getItem("cml-chat-id");
    if (!id) {
      id = `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem("cml-chat-id", id);
    }
    return id;
  });
  const listRef = useRef(null);
  const [bannerOpen, setBannerOpen] = useState(false);

  useEffect(() => {
    const onConsent = (e) => setBannerOpen(!!e.detail);
    setBannerOpen(document.body.dataset.consentOpen === "1");
    window.addEventListener("cil-consent", onConsent);
    return () => window.removeEventListener("cil-consent", onConsent);
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/api/concierge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const chunk of parts) {
          if (!chunk.startsWith("event: delta")) continue;
          const dataLine = chunk.split("\n").find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          const { text: delta } = JSON.parse(dataLine.slice(6));
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + delta };
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "I beg your pardon — the concierge seems unreachable this moment. WhatsApp us on +353 86 251 8252 and we will help straight away.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="concierge-toggle"
        aria-label="Chat with the island concierge"
        className={`fixed left-5 z-50 flex h-14 items-center gap-2.5 rounded-full bg-navy-2 border border-gold/50 px-5 text-white shadow-[0_8px_32px_rgba(10,25,47,0.45)] transition-[bottom,transform] duration-300 hover:scale-105 ${bannerOpen ? "max-lg:hidden bottom-[248px] lg:bottom-5" : "bottom-20 lg:bottom-5"}`}
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute h-full w-full rounded-full bg-gold opacity-75 animate-ping" />
          <span className="h-2.5 w-2.5 rounded-full bg-gold" />
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">
          {open ? "Close" : "Ask the Concierge"}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="concierge-panel"
            className={`fixed left-5 z-50 flex h-[520px] w-[calc(100vw-40px)] max-w-sm flex-col border border-gold/30 bg-navy-2 shadow-[0_24px_64px_rgba(10,25,47,0.6)] transition-[bottom] duration-300 ${bannerOpen ? "max-lg:hidden bottom-[264px] lg:bottom-5" : "bottom-24 lg:bottom-5"}`}
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between border-b border-gold/20 px-5 py-4">
              <div>
                <p className="font-display text-xl text-ivory">The Concierge</p>
                <p className="text-[10px] uppercase tracking-widest2 text-gold-2">Collanmore Island Lodge</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="concierge-close" aria-label="Close chat" className="text-ivory/60 hover:text-gold-2">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="concierge-messages">
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <p
                    className={`max-w-[85%] px-4 py-2.5 text-sm font-light leading-relaxed ${
                      m.role === "user"
                        ? "bg-gold text-navy"
                        : "border border-ivory/15 text-ivory/90"
                    }`}
                    data-testid={`concierge-msg-${m.role}`}
                  >
                    {m.text || "…"}
                  </p>
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {QUICK.map((q) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      data-testid={`concierge-quick-${QUICK.indexOf(q) + 1}`}
                      className="border border-gold/40 px-3 py-1.5 text-[11px] font-light text-gold-2 transition-colors hover:bg-gold hover:text-navy"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 border-t border-gold/20 p-3"
              data-testid="concierge-input-form"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about the island…"
                data-testid="concierge-input"
                className="flex-1 border-b border-ivory/25 bg-transparent py-2 text-sm font-light text-ivory outline-none placeholder:text-ivory/40 focus:border-gold"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="concierge-send"
                aria-label="Send message"
                className="flex h-10 w-10 items-center justify-center bg-gold text-navy transition-opacity disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
